import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChartV2Horizon } from '../types/chart-v2.types';
import type { DataExtent } from '../utils/data-extent';

/**
 * Minimum "pinned to the right edge" tolerance in milliseconds. Without
 * a floor, chunky tick deltas would still keep the auto-pan locked even
 * when the user scrolled away — but very small deltas could lose the
 * pin entirely. 1 second covers typical sub-minute streaming gaps.
 */
const FOLLOW_THRESHOLD_FLOOR_MS = 1000;

export interface UseSharedDomainOptions {
  horizon: ChartV2Horizon;
  dataExtent: DataExtent;
  initialDomain: DataExtent;
  onDomainChange?: (range: DataExtent) => void;
}

export interface UseSharedDomainResult {
  sharedDomain: [number, number];
  setSharedDomain: (next: [number, number]) => void;
  handleDomainChange: (range: DataExtent) => void;
  handleDomainReset: () => void;
}

/**
 * Owns the chart's currently-visible x-range.
 *
 * - Resets to `initialDomain` when the `horizon` prop changes.
 * - Auto-pans the window forward when the latest data end advances while the
 *   user is already pinned to the right edge (matching how streaming quotes
 *   behave in real charts).
 */
export const useSharedDomain = ({
  horizon,
  dataExtent,
  initialDomain,
  onDomainChange,
}: UseSharedDomainOptions): UseSharedDomainResult => {
  const [sharedDomain, setSharedDomain] = useState<[number, number]>(() => [
    initialDomain.start,
    initialDomain.end,
  ]);

  const initialDomainRef = useRef(initialDomain);

  initialDomainRef.current = initialDomain;

  const prevHorizonRef = useRef(horizon);

  useEffect(() => {
    if (prevHorizonRef.current !== horizon) {
      prevHorizonRef.current = horizon;
      setSharedDomain([
        initialDomainRef.current.start,
        initialDomainRef.current.end,
      ]);
    }
  }, [horizon]);

  const prevDataEndRef = useRef(dataExtent.end);

  useEffect(() => {
    const prevEnd = prevDataEndRef.current;
    const newEnd = dataExtent.end;

    prevDataEndRef.current = newEnd;

    if (newEnd <= prevEnd) {
      return;
    }

    const delta = newEnd - prevEnd;
    const followThreshold = Math.max(delta * 2, FOLLOW_THRESHOLD_FLOOR_MS);

    setSharedDomain(current => {
      const wasFollowing = current[1] >= prevEnd - followThreshold;

      if (!wasFollowing) {
        return current;
      }

      return [current[0] + delta, current[1] + delta];
    });
  }, [dataExtent.end]);

  const handleDomainChange = useCallback(
    (range: DataExtent) => {
      setSharedDomain([range.start, range.end]);
      onDomainChange?.(range);
    },
    [onDomainChange]
  );

  const handleDomainReset = useCallback(() => {
    const ref = initialDomainRef.current;

    setSharedDomain([ref.start, ref.end]);
    onDomainChange?.({ start: ref.start, end: ref.end });
  }, [onDomainChange]);

  return {
    sharedDomain,
    setSharedDomain,
    handleDomainChange,
    handleDomainReset,
  };
};
